import type React from 'react';

import { Modal, ModalBody, ModalFooter, ModalHeader } from '@change/ds-core-ui';

import { useI18n, withI18nPlaceholders } from '@change-corgi/core-react/i18n';

import I18N_PLACEHOLDERS from '#config/i18n/placeholders/psf/promoteOrShare/facebook-post-sheet.yml';

import { CaptionPreview } from './components/CaptionPreview';
import { DestinationChoice } from './components/DestinationChoice';
import { FeedStep } from './components/FeedStep';
import { GroupCardList } from './components/GroupCardList';
import { renderFooter, renderStepCta } from './footer';
import { useSheetSteps, useViewedOnce } from './hooks';
import type { FacebookPostSheetProps } from './types';

function noop(): void {
	/* see the `onClose` comment on the Modal below */
}

type StepBodyProps = Pick<FacebookPostSheetProps, 'caption' | 'petition' | 'copied' | 'groupDestination'> & {
	steps: ReturnType<typeof useSheetSteps>;
	copySuccessLabel: string;
};

/** The body of the current step: the destination choice, the group cards, or the `variant_1` feed preview. */
const StepBody: React.FC<StepBodyProps> = ({
	caption,
	petition,
	copied,
	groupDestination,
	steps,
	copySuccessLabel,
}) => {
	if (steps.step === 'destination') {
		return (
			<DestinationChoice
				preview={<CaptionPreview caption={caption} petition={petition} compact />}
				copied={copied}
				copySuccessLabel={copySuccessLabel}
				value={steps.destination}
				onChange={steps.setDestination}
			/>
		);
	}
	if (steps.step === 'groups') {
		if (!groupDestination) return null;
		return (
			<GroupCardList
				groups={groupDestination.groups}
				selectedUrl={steps.selectedGroup?.url ?? null}
				onSelect={steps.selectGroup}
				onCardViewed={groupDestination.onGroupCardViewed}
			/>
		);
	}
	return <FeedStep caption={caption} petition={petition} copied={copied} copySuccessLabel={copySuccessLabel} />;
};

/**
 * EXPERIMENT: psf_promote_or_share_facebook_post_sheet_experiment.
 * The Facebook post sheet. In `variant_1`: a "Copied to clipboard" pill when the Facebook tap copied
 * the post, a preview of the finished post, and a CTA that opens the Facebook composer (and copies
 * the post only if the tap's copy failed). In `variant_2` (with `groupDestination`), the sheet opens on
 * the same pill and preview, above a feed-or-group choice. Continue with the feed picked opens the
 * composer, the way the `variant_1` CTA does. The group choice leads to the group cards, with no back
 * button and no second pill: a card tap picks the group, and the group CTA opens it.
 * Purely presentational: `ShareSlideUp` owns the open state, and `useFacebookPostSheet` owns the copy,
 * the composer and group opens, and the tracking.
 */
const FacebookPostSheetBase: React.FC<FacebookPostSheetProps> = ({
	isOpen,
	caption,
	petition,
	copied,
	onPost,
	onDismiss,
	onView,
	groupDestination,
}) => {
	const { translate } = useI18n();
	const steps = useSheetSteps(isOpen, groupDestination, onPost);
	const { step } = steps;
	useViewedOnce(isOpen, onView);

	const sheetTitle = translate('corgi.psf.promote_or_share.facebook_post_sheet.title');
	const groupTitle = translate('corgi.psf.promote_or_share.facebook_post_sheet.group_title');
	const title = step === 'groups' ? groupTitle : sheetTitle;
	const copySuccessLabel = translate('corgi.psf.promote_or_share.facebook_post_sheet.copy_success');
	const cta = renderStepCta({
		step,
		translate,
		onContinue: steps.continueToDestination,
		onPost,
		selectedGroup: steps.selectedGroup,
		onPostToGroup: groupDestination?.onPostToGroup,
	});

	return (
		<Modal
			isOpen={isOpen}
			transformToSheet
			// `onClose` only exists to make the DS render the X; it must stay a no-op so dismissal does
			// not double-fire. The X, ESC and an outside tap all arrive at `onOpenChange`.
			onClose={noop}
			closeButtonAriaLabel={translate('corgi.components.modal.close_button')}
			onOpenChange={(open) => {
				if (!open) onDismiss();
			}}
			aria-label={title}
			className="z-100"
		>
			<ModalHeader title={title} />

			<ModalBody className="max-h-[calc(85dvh-80px)]" data-testid="facebook-post-sheet-body">
				<StepBody
					caption={caption}
					petition={petition}
					copied={copied}
					groupDestination={groupDestination}
					steps={steps}
					copySuccessLabel={copySuccessLabel}
				/>
			</ModalBody>

			{/* A render function, so the footer does not close the modal on its own after the tap: the
			    owner closes it, and a close that the footer started would read as a dismissal. */}
			<ModalFooter>{() => renderFooter(cta, translate)}</ModalFooter>
		</Modal>
	);
};

export const FacebookPostSheet = withI18nPlaceholders(I18N_PLACEHOLDERS, FacebookPostSheetBase);
